import { ReactNode } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { cn } from '@/lib/utils';

/**
 * Shared chrome for every public page outside of Home.
 * - HomeHeader is sticky + frosted-glass; pages set `transparent` only when their
 *   first section is dark enough to support a white-text header.
 * - `hero` slot replaces the typical "title bar" pattern with a real dark hero.
 * - Wrapping <main> gets `min-h-screen` and `bg-white` so any content section
 *   below the hero sits on a clean canvas.
 */
export default function PageShell({
  children,
  hero,
  headerTransparent = true,
  className,
}: {
  children: ReactNode;
  hero?: ReactNode;
  headerTransparent?: boolean;
  className?: string;
}) {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <HomeHeader transparent={headerTransparent} />
      <main className={cn('min-h-[60vh]', className)}>
        {hero}
        {children}
      </main>
      <Footer />
    </div>
  );
}

/**
 * Reusable dark gradient hero band. Designed to sit directly under the sticky
 * header — pads top to clear the header and bottom for breathing room. Optional
 * right-rail (image, illustration, mock card) is centered on its own column.
 */
export function PageHero({
  eyebrow,
  title,
  lead,
  actions,
  rail,
  size = 'md',
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  actions?: ReactNode;
  rail?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
}) {
  const padY =
    size === 'sm'
      ? 'pt-28 md:pt-36 pb-16 md:pb-20'
      : size === 'lg'
      ? 'pt-32 md:pt-40 pb-24 md:pb-32'
      : 'pt-32 md:pt-40 pb-20 md:pb-28';
  return (
    <section className="relative overflow-hidden bg-[#070b18] text-white">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(30,64,175,0.45),transparent_60%),radial-gradient(80%_60%_at_100%_100%,rgba(250,204,21,0.18),transparent_55%)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.07] [background-image:radial-gradient(rgba(255,255,255,0.7)_1px,transparent_1px)] [background-size:24px_24px]"
      />
      <div
        aria-hidden
        className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-3xl"
        style={{ animation: 'blobFloat1 18s ease-in-out infinite' }}
      />
      <div
        aria-hidden
        className="absolute -bottom-32 right-0 h-[460px] w-[460px] rounded-full bg-yellow-400/10 blur-3xl"
        style={{ animation: 'blobFloat2 22s ease-in-out infinite' }}
      />

      <div className={cn('relative mx-auto max-w-7xl px-6', padY)}>
        <div className={cn('grid items-center gap-10 lg:gap-16', rail ? 'lg:grid-cols-12' : 'lg:grid-cols-1')}>
          <div className={cn(rail ? 'lg:col-span-7' : 'max-w-4xl')}>
            {eyebrow && (
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-yellow-300/90">
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-400 animate-pulse" />
                {eyebrow}
              </div>
            )}
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[0.95] text-balance">
              {title}
            </h1>
            {lead && (
              <p className="mt-7 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed">
                {lead}
              </p>
            )}
            {actions && <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:items-center">{actions}</div>}
          </div>
          {rail && <div className="lg:col-span-5 relative">{rail}</div>}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" aria-hidden />
    </section>
  );
}

export function Eyebrow({
  children,
  tone = 'blue',
  className,
}: {
  children: ReactNode;
  tone?: 'blue' | 'yellow';
  className?: string;
}) {
  return (
    <div
      className={cn(
        'text-xs uppercase tracking-widest font-semibold',
        tone === 'yellow' ? 'text-yellow-300/90' : 'text-blue-700',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function SectionHead({
  eyebrow,
  title,
  lead,
  tone = 'light',
  align = 'left',
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  tone?: 'light' | 'dark';
  align?: 'left' | 'center';
  className?: string;
}) {
  const titleColor = tone === 'dark' ? 'text-white' : 'text-slate-900';
  const leadColor = tone === 'dark' ? 'text-slate-300' : 'text-slate-500';
  return (
    <div className={cn(align === 'center' && 'text-center mx-auto max-w-3xl', className)}>
      {eyebrow && (
        <Eyebrow tone={tone === 'dark' ? 'yellow' : 'blue'} className={align === 'center' ? 'inline-block' : undefined}>
          {eyebrow}
        </Eyebrow>
      )}
      <h2
        className={cn(
          'mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]',
          titleColor,
        )}
      >
        {title}
      </h2>
      {lead && <p className={cn('mt-5 text-lg md:text-xl leading-relaxed max-w-2xl', leadColor, align === 'center' && 'mx-auto')}>{lead}</p>}
    </div>
  );
}

/** Light-surface section wrapper with consistent vertical rhythm. */
export function LightSection({
  children,
  bg = 'white',
  className,
}: {
  children: ReactNode;
  bg?: 'white' | 'gray';
  className?: string;
}) {
  return (
    <section className={cn(bg === 'gray' ? 'bg-slate-50' : 'bg-white', className)}>
      <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">{children}</div>
    </section>
  );
}

/** Dark-surface section wrapper. */
export function DarkSection({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn('relative overflow-hidden bg-[#070b18] text-white', className)}>
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(60%_80%_at_50%_0%,rgba(30,64,175,0.4),transparent_60%)]"
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28">{children}</div>
    </section>
  );
}

/** Surface-aware card for feature grids, bento layouts, and standalone callouts. */
export function FeatureCard({
  icon,
  title,
  body,
  className,
  tone = 'light',
}: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  className?: string;
  tone?: 'light' | 'dark';
}) {
  if (tone === 'dark') {
    return (
      <div
        className={cn(
          'rounded-3xl bg-white/[0.04] border border-white/10 p-7 hover:bg-white/[0.07] transition-colors backdrop-blur-sm',
          className,
        )}
      >
        {icon && (
          <div className="h-11 w-11 rounded-xl bg-yellow-400/15 text-yellow-300 grid place-items-center border border-yellow-400/20">
            {icon}
          </div>
        )}
        <h3 className="mt-5 text-xl font-bold tracking-tight text-white">{title}</h3>
        {body && <p className="mt-2 text-slate-300 leading-relaxed">{body}</p>}
      </div>
    );
  }
  return (
    <div
      className={cn(
        'rounded-3xl bg-white border border-slate-200/70 p-7 hover:border-slate-300 hover:-translate-y-1 transition-all duration-300 shadow-sm hover:shadow-lg',
        className,
      )}
    >
      {icon && (
        <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center">{icon}</div>
      )}
      <h3 className="mt-5 text-xl font-bold tracking-tight text-slate-900">{title}</h3>
      {body && <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>}
    </div>
  );
}

/** Primary CTA button (yellow). For dark surfaces or as the lead action. */
export function PrimaryCTA({
  children,
  to,
  className,
}: {
  children: ReactNode;
  to?: string;
  className?: string;
}) {
  const cls = cn(
    'inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 text-slate-900 px-7 py-4 text-base font-semibold shadow-2xl shadow-yellow-500/30 transition-all active:scale-[0.98]',
    className,
  );
  if (to) {
    return (
      <a href={to} className={cls}>
        {children}
      </a>
    );
  }
  return <button type="button" className={cls}>{children}</button>;
}

/** Ghost / secondary CTA. */
export function SecondaryCTA({
  children,
  to,
  className,
  tone = 'dark',
}: {
  children: ReactNode;
  to?: string;
  className?: string;
  tone?: 'dark' | 'light';
}) {
  const cls = cn(
    'inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-medium transition-colors',
    tone === 'dark'
      ? 'border border-white/20 bg-white/5 hover:bg-white/10 backdrop-blur text-white'
      : 'border border-slate-300 bg-white hover:bg-slate-50 text-slate-900',
    className,
  );
  if (to) {
    return (
      <a href={to} className={cls}>
        {children}
      </a>
    );
  }
  return <button type="button" className={cls}>{children}</button>;
}

/** "Athletes pay $0 / You receive 100%" style end-of-section flourish. */
export function ClosingBanner({
  eyebrow,
  title,
  lead,
  primary,
  secondary,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden bg-[#070b18] text-white">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(100%_80%_at_50%_0%,rgba(30,64,175,0.4),transparent_60%),radial-gradient(60%_60%_at_100%_100%,rgba(250,204,21,0.15),transparent_60%)]"
      />
      <div className="relative mx-auto max-w-5xl px-6 py-24 md:py-32 text-center">
        {eyebrow && (
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wider uppercase text-yellow-300/90">
            {eyebrow}
          </div>
        )}
        <h2 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[0.98] text-balance">
          {title}
        </h2>
        {lead && <p className="mt-7 max-w-2xl mx-auto text-lg md:text-xl text-slate-300 leading-relaxed">{lead}</p>}
        {(primary || secondary) && (
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            {primary}
            {secondary}
          </div>
        )}
      </div>
    </section>
  );
}
